import { useEffect, useState } from 'react';
import { get } from '../api';
import type { FilaArticulo } from '../tipos';
import { Dist, Vacio } from '../components/ui';
import { formatear } from '../components/GraficoPlan';

/**
 * Todos los artículos del plan, ordenados por lo bajo que llega su stock.
 *
 * La columna que manda es `min_projected`: el punto más bajo al que llega el
 * stock proyectado en todo el horizonte. Es la respuesta a «¿qué me va a doler
 * primero?», que es la pregunta que se hace quien abre esta pantalla.
 */
export function Articulos({
  runId,
  onVerArticulo,
}: {
  runId: string;
  onVerArticulo: (codigo: string, almacen: string) => void;
}): JSX.Element {
  const [filas, setFilas] = useState<FilaArticulo[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [soloRiesgo, setSoloRiesgo] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const params = new URLSearchParams();
    if (busqueda.trim()) params.set('search', busqueda.trim());
    if (soloRiesgo) params.set('risk', 'true');
    const t = setTimeout(() => {
      void get<{ items: FilaArticulo[] }>(`/runs/${runId}/items?${params}`)
        .then((r) => vigente && setFilas(r.items))
        .finally(() => vigente && setCargando(false));
    }, 180);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [runId, busqueda, soloRiesgo]);

  return (
    <div>
      <div className="filtros">
        <input placeholder="Buscar artículo…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} style={{ minWidth: 220 }} />
        <button className="secundario" aria-pressed={soloRiesgo} onClick={() => setSoloRiesgo((v) => !v)}>
          Solo los que bajan del mínimo
        </button>
        <span className="sutil">{filas.length} artículo(s)</span>
      </div>

      <div className="tarjeta" style={{ padding: 0 }}>
        <div className="tabla-scroll">
          <table>
            <thead>
              <tr>
                <th>Artículo</th>
                <th>Alm.</th>
                <th>Origen</th>
                <th className="num">Stock</th>
                <th className="num">Mínimo</th>
                <th className="num">Punto más bajo</th>
                <th>Cómo evoluciona</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const bajaDelMinimo = f.min_projected < f.item.safetyStock;
                return (
                  <tr key={`${f.item_code}/${f.warehouse}`}>
                    <td>
                      <button
                        onClick={() => onVerArticulo(f.item_code, f.warehouse)}
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--serie-1)', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <strong>{f.item_code}</strong>
                      </button>
                      <div className="sutil">{f.item.name}</div>
                    </td>
                    <td className="mono">{f.warehouse}</td>
                    <td>
                      {f.item.excluded ? (
                        <Dist tono="neutro" icono="—">Fuera del MRP</Dist>
                      ) : f.item.procurement === 'make' ? (
                        <Dist tono="neutro" icono="⚙">Fabricar</Dist>
                      ) : (
                        <Dist tono="neutro" icono="↓">Comprar</Dist>
                      )}
                    </td>
                    <td className="num mono">{formatear(f.on_hand)}</td>
                    <td className="num mono">{formatear(f.item.safetyStock)}</td>
                    <td className="num mono" style={{ color: bajaDelMinimo ? 'var(--critico)' : undefined, fontWeight: bajaDelMinimo ? 600 : undefined }}>
                      {formatear(f.min_projected)}
                    </td>
                    <td><Chispa buckets={f.buckets} minimo={f.item.safetyStock} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {cargando ? <Vacio>Cargando…</Vacio> : filas.length === 0 ? <Vacio>Ningún artículo con estos filtros.</Vacio> : null}
      </div>
    </div>
  );
}

/**
 * Minigráfico del stock proyectado.
 *
 * Una sola serie y sin ejes: aquí solo interesa la FORMA (si cae, si se
 * recupera, cuándo), y el número exacto está en las columnas de al lado. La
 * línea del mínimo se dibuja para que la caída signifique algo.
 */
function Chispa({ buckets, minimo }: { buckets: Array<{ projected: number }>; minimo: number }): JSX.Element {
  const w = 132;
  const h = 30;
  if (buckets.length < 2) return <span className="sutil">—</span>;

  const valores = buckets.map((b) => b.projected);
  const max = Math.max(...valores, minimo, 0);
  const min = Math.min(...valores, 0);
  const rango = max - min || 1;
  const y = (v: number): number => h - ((v - min) / rango) * (h - 4) - 2;
  const x = (i: number): number => (i / (buckets.length - 1)) * w;
  const d = valores.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <svg width={w} height={h} aria-hidden="true" style={{ display: 'block' }}>
      {minimo > 0 && <line x1="0" x2={w} y1={y(minimo)} y2={y(minimo)} stroke="var(--aviso)" strokeWidth="1" strokeDasharray="2 3" />}
      <line x1="0" x2={w} y1={y(0)} y2={y(0)} stroke="var(--linea)" strokeWidth="1" />
      <path d={d} fill="none" stroke="var(--serie-1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
